import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { downloadFile } from "@/lib/exportCsv";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { PRIMARY_BTN_CLS, SECONDARY_BTN_CLS } from "@/components/premium";

/**
 * Premium-only customer CSV import.
 *
 * Two-step flow:
 *   1. User picks file → we POST with ?dry_run=true and show a preview
 *      (would-create / would-skip / invalid + first 10 rows).
 *   2. User confirms → we POST again without dry_run and show the result.
 *
 * The dialog disables itself entirely for non-Premium owners (the trigger
 * button is rendered with an "Premium"-badge so they know what's missing).
 * Backend also enforces the gate via 402.
 */
export function CustomerImportDialog({ isPremium, onDone }) {
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { would_create, would_skip, invalid, preview: [...] }
  const [committed, setCommitted] = useState(null); // { imported, errors }
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setCommitted(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const pick = () => fileRef.current?.click();

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setCommitted(null);
    await runImport(f, true);
  };

  const runImport = async (f, dryRun) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const res = await api.post(
        `/customers/import${dryRun ? "?dry_run=true" : ""}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (dryRun) {
        setPreview(res.data);
      } else {
        setCommitted(res.data);
        if ((res.data.imported || 0) > 0) {
          toast.success(`${res.data.imported} kunder importert`);
          onDone?.();
        } else {
          toast.message("Ingen nye kunder å importere");
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Importen feilet";
      if (status === 402) toast.error(detail);
      else if (status === 413) toast.error(detail);
      else if (status === 400) toast.error(detail);
      else toast.error(detail);
      // Reset on hard error so the user can pick another file
      if (status === 400 || status === 413) reset();
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = () => {
    if (!file) return;
    runImport(file, false);
  };

  const closeAndReset = () => { setOpen(false); reset(); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); setOpen(o); }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={SECONDARY_BTN_CLS}
          data-testid="import-customers-trigger"
          disabled={!isPremium}
          title={isPremium ? "" : "Premium-funksjon"}
        >
          <Upload className="w-4 h-4" strokeWidth={1.75} />
          Importer kunder
          {!isPremium && (
            <span className="ml-1.5 text-[10px] uppercase tracking-widest font-semibold text-emerald-500">
              Premium
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-lg" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" strokeWidth={1.75} />
            Importer kunder (CSV)
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Last opp en CSV-fil med kundene dine. Vi viser en forhåndsvisning før vi importerer noe.
          </DialogDescription>
        </DialogHeader>

        <input
          type="file"
          ref={fileRef}
          accept=".csv,text/csv"
          className="hidden"
          onChange={handleFileChange}
          data-testid="import-file-input"
        />

        {/* Stage 1 — pick file */}
        {!preview && !committed && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Last opp en CSV-fil med kundene dine. Vi støtter kolonnene <strong>Navn</strong>,
              <strong> Telefon</strong>, <strong>Epost</strong> og <strong>Notat</strong>.
              Duplikater (samme telefon eller e-post) hoppes over automatisk.
            </p>
            <div className="rounded-xl border border-dashed border-border p-6 text-center">
              <Button
                onClick={pick}
                className={PRIMARY_BTN_CLS}
                disabled={busy}
                data-testid="import-pick-file-btn"
              >
                <Upload className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                {busy ? "Leser fil..." : "Velg CSV-fil"}
              </Button>
              <p className="mt-3 text-xs text-muted-foreground">
                Maks 2 MB. Excel-eksport (UTF-8, kommadelt eller semikolon) støttes.
              </p>
              <button
                type="button"
                className="mt-3 text-xs text-primary hover:underline inline-flex items-center gap-1"
                onClick={() => downloadFile(
                  "/customers/import/example.csv",
                  "clientflow-kundeimport-mal.csv",
                  "eksempel-CSV",
                )}
                data-testid="import-example-csv-link"
              >
                <FileSpreadsheet className="w-3 h-3" strokeWidth={1.75} />
                Last ned eksempel-CSV
              </button>
            </div>
          </div>
        )}

        {/* Stage 2 — preview */}
        {preview && !committed && (
          <div className="space-y-3" data-testid="import-preview">
            <div className="grid grid-cols-3 gap-2">
              <SummaryStat label="Importeres" value={preview.would_create} tone="ok" testid="import-stat-create" />
              <SummaryStat label="Hoppet over" value={preview.would_skip} tone="warn" testid="import-stat-skip" />
              <SummaryStat label="Ugyldige" value={preview.invalid} tone="warn" testid="import-stat-invalid" />
            </div>
            {preview.preview?.length > 0 && (
              <div className="rounded-xl border border-border overflow-hidden">
                <p className="px-3 py-2 text-xs uppercase tracking-widest font-semibold text-muted-foreground bg-muted/50">
                  Forhåndsvisning (første {preview.preview.length})
                </p>
                <ul className="divide-y divide-border max-h-48 overflow-auto text-sm">
                  {preview.preview.map((row, i) => (
                    <li key={i} className="px-3 py-2 flex items-center gap-2" data-testid="import-preview-row">
                      <span className="font-medium truncate flex-1">{row.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {[row.phone, row.email].filter(Boolean).join(" · ") || "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {preview.would_create === 0 && (
              <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm p-3 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Ingen nye kunder å importere. Filen inneholder kun eksisterende kunder eller ugyldige rader.</span>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={reset}
                className={SECONDARY_BTN_CLS}
                disabled={busy}
                data-testid="import-back-btn"
              >
                Velg annen fil
              </Button>
              <Button
                onClick={onConfirm}
                className={PRIMARY_BTN_CLS}
                disabled={busy || preview.would_create === 0}
                data-testid="import-confirm-btn"
              >
                {busy ? "Importerer..." : `Importer ${preview.would_create} kunder`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Stage 3 — committed */}
        {committed && (
          <div className="space-y-3" data-testid="import-result">
            <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300 text-sm p-3 flex gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                <strong>{committed.imported}</strong> kunder importert.
                {committed.errors?.length > 0 && (
                  <> {committed.errors.length} feil oppstod.</>
                )}
              </span>
            </div>
            {committed.errors?.length > 0 && (
              <ul className="text-xs text-muted-foreground max-h-32 overflow-auto space-y-0.5">
                {committed.errors.map((e, i) => (
                  <li key={i}>• {e}</li>
                ))}
              </ul>
            )}
            <DialogFooter className="gap-2">
              <Button onClick={closeAndReset} className={PRIMARY_BTN_CLS} data-testid="import-close-btn">
                Ferdig
              </Button>
            </DialogFooter>
          </div>
        )}

        {!isPremium && (
          <p className="text-xs text-muted-foreground border-t border-border pt-3 mt-1 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Kundeimport er en Premium-funksjon.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryStat({ label, value, tone, testid }) {
  const toneCls =
    tone === "ok"
      ? "text-emerald-700 dark:text-emerald-400"
      : "text-amber-700 dark:text-amber-400";
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-3 text-center" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{label}</div>
      <div className={`mt-0.5 font-heading text-2xl font-bold ${toneCls}`}>{value ?? 0}</div>
    </div>
  );
}
